/**
 * Agentic-room composer — the single bottom-of-room widget the user
 * interacts with. Wraps `WorkbenchPromptCard` so the visual chrome stays
 * identical to the Network Workbench prompt.
 *
 * Three composer states (see `composerState.js`):
 *   - IDLE                — start a new run via `pool_execute`.
 *   - RUNNING             — locked for everyone (incl. owner).
 *   - AWAITING_APPROVAL   — Approve / Deny / Modify in the second deck;
 *                            prompt only enables after Modify click.
 *
 * Send semantics:
 *   - IDLE → calls `onSendPrompt(text, { stagingId, attachments })`.
 *   - AWAITING_APPROVAL + modifyMode → calls `onSendModify(text, { ... })`.
 *
 * The composer owns its own draft (text + attachments). The parent owns
 * approval lease state, busy flags, and persisted UI state. This split
 * keeps the room thin and the composer easy to test in isolation.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Paperclip, Check, X as XIcon, Pencil } from 'lucide-react';
import WorkbenchPromptCard from '@stepflow/ui-shared/components/WorkbenchPromptCard.jsx';
import ComposerPlusMenu from '@stepflow/ui-shared/components/ComposerPlusMenu.jsx';
import ComposerHint from '@stepflow/ui-shared/components/ComposerHint.jsx';
import { getRoomContext } from '@stepflow/ui-shared/services/dataPlaneApi';
import WorkbenchPromptAlertBand from '@stepflow/ui-shared/components/WorkbenchPromptAlertBand.jsx';
import BuilderInteractionBand from '../BuilderInteractionBand.jsx';
import { useWorkbenchPromptFieldHeight } from '@/hooks/useWorkbenchPromptFieldHeight.js';
import {
  getWorkbenchPromptInputClassName,
  WORKBENCH_PROMPT_FIELD_STYLE,
} from '@/utils/workbenchPromptFieldClassNames.js';
import { WORKBENCH_OUTLINE_BTN_TOOLBAR } from '@/utils/workbenchOutlineButtonClassNames.js';
import {
  COMPOSER_STATES,
  canSendNow,
  deriveComposerState,
  isPromptEditable,
  placeholderFor,
} from './composerState.js';
import { useChatAttachments } from '../../hooks/useChatAttachments.js';
import AttachmentChips from './AttachmentChips.jsx';

const COMPOSER_TIP_BULLETS = [
  <><kbd className="rounded border border-gray-300 bg-gray-100 px-1 py-0.5">Enter</kbd> sends the message</>,
  <><kbd className="rounded border border-gray-300 bg-gray-100 px-1 py-0.5">Shift+Enter</kbd> inserts a new line</>,
  <>Use the <span className="font-medium">+</span> to attach files</>,
];

const COMPOSER_PROMPT_TIP_KEY = 'stepflow:composer-prompt-tip-dismissed';

/**
 * @typedef {Object} ComposerProps
 * @property {string} networkId
 * @property {string} poolId
 * @property {boolean} canExecute               Caller has permission to start a new run.
 * @property {string|null} uiState              Most recent `UI_STATE.uiState`.
 * @property {{ taskToken: string, taskId: string, agentName?: string }|null} pendingApproval
 * @property {boolean} iHoldClaim
 * @property {boolean} peerHoldsClaim
 * @property {string} [peerLabel]
 * @property {boolean} approvalBusy
 * @property {boolean} sending
 * @property {(text: string, opts: { stagingId: string, attachments: object[], correlationId: string }) => Promise<void>} onSendPrompt
 * @property {() => Promise<void>} onClaim
 * @property {() => Promise<void>} onApprove
 * @property {() => Promise<void>} onDeny
 * @property {(text: string, opts: { stagingId: string, attachments: object[] }) => Promise<void>} onSendModify
 * @property {() => void} onDelegate
 * @property {() => Promise<void>} onEscalate
 */

export default function Composer({
  networkId,
  poolId,
  canExecute,
  uiState,
  pendingApproval,
  iHoldClaim,
  peerHoldsClaim,
  peerLabel,
  approvalBusy = false,
  sending = false,
  /** Lifecycle freeze (M5) — true when the room is PAUSED, RETIRED, or
   *  ARCHIVED. Disables every send / claim / approval action and
   *  surfaces ``frozenReason`` as the textarea placeholder. */
  frozen = false,
  /** Tooltip / placeholder copy for the frozen variant. */
  frozenReason = '',
  /** Pre-flight MCP gate: when set, an enabled authenticated MCP has no
   *  credential for this room — Send is blocked and this copy is shown. */
  mcpGateReason = '',
  onSendPrompt,
  onClaim,
  onApprove,
  onDeny,
  onSendModify,
  onDelegate,
  onEscalate,
  builderCliWait = null,
  onBuilderCliHandled,
  canRespondToBuilderApproval: canRespondApproval = true,
  /** ALWAYS-PRESENT STOP. When a builder run is active this is its runId; the
   *  composer shows a persistent Stop control (both background + interactive)
   *  right where the user is, so a runaway can be killed anytime. */
  activeBuilderRunId = null,
  stoppingRun = false,
  onStopRun,
  /** Visual width — ``"center"`` keeps the legacy ``mx-auto max-w-3xl``
   *  cap; ``"left"`` spans the full panel so the prompt aligns with the
   *  full-width chat stream above it. Default ``"center"`` preserves
   *  every existing caller's appearance. */
  align = 'center',
  /** Verbose (the Shared ``__roomContext__`` toggle, default OFF). When OFF the
   *  under-bar shows ONLY the AI-guidance icon — its popover already carries the
   *  completion basis + suggested-next prompts, so the inline copy is redundant
   *  chrome. When ON the inline completion-basis line + "Next:" quick-prompt
   *  chips are also surfaced. Mirrors the run-block cost telemetry gate. */
  verbose = false,
}) {
  const composerState = deriveComposerState({ uiState, pendingApproval });
  // While the room is RUNNING, guidance (working premise + next-suggestions +
  // completion) is stale/irrelevant — hide it; it returns when the room is IDLE.
  const roomIsRunning = composerState === COMPOSER_STATES.RUNNING;

  const [prompt, setPrompt] = useState('');
  const [modifyMode, setModifyMode] = useState(false);

  // Live room guidance (advisory): suggested next prompts + completion, from the
  // room-context synthesis (CONTEXT.md §15d). Surfaced in the "i" popover; null
  // when Room Context is off or no synthesis has run yet. Best-effort — a failed
  // fetch just leaves the static tips. Refetches when the run finishes (uiState
  // settles) so suggestions reflect the latest state.
  const [roomGuidance, setRoomGuidance] = useState(null);
  const [roomGuidanceAt, setRoomGuidanceAt] = useState('');
  // The room's WORKING PREMISE (established canon/spine) from the same synthesis.
  // Shown in the composer band so the user sees what the room is anchored to —
  // the same text the "Room guidance" popover renders under "Working premise".
  const [roomPremise, setRoomPremise] = useState('');
  useEffect(() => {
    let cancelled = false;
    if (!networkId || !poolId) return undefined;
    getRoomContext(networkId, { pool: poolId })
      .then((res) => {
        if (cancelled) return;
        const g = res?.summary?.guidance || null;
        setRoomGuidance(g && (g.suggestedPrompts?.length || g.completion) ? g : null);
        setRoomPremise(String(g?.workingPremise || '').trim());
        setRoomGuidanceAt(res?.summary?.extractedAt || '');
      })
      .catch(() => { if (!cancelled) { setRoomGuidance(null); setRoomPremise(''); } });
    return () => { cancelled = true; };
    // Refetch on room switch + when a run settles (uiState leaves RUNNING).
  }, [networkId, poolId, uiState]);
  const [composerTipDismissed, setComposerTipDismissed] = useState(() => {
    try {
      return typeof sessionStorage !== 'undefined' && sessionStorage.getItem(COMPOSER_PROMPT_TIP_KEY) === '1';
    } catch {
      return false;
    }
  });
  const promptFieldRef = useRef(null);
  const fileInputRef = useRef(null);
  const promptHeight = useWorkbenchPromptFieldHeight(promptFieldRef, prompt, { maxHeightPx: 200 });
  // Past ~3 lines flip the pill → two-deck card (text top, buttons below).
  // Gate on the value (empty ⇒ single-row) so it snaps back the instant the
  // prompt is sent/cleared, not after the next height re-measure.
  const composerMultiline = !!String(prompt || '').trim() && promptHeight > 64;

  const attachments = useChatAttachments(networkId);

  // Leaving AWAITING_APPROVAL clears any half-typed modify draft so the
  // next IDLE turn doesn't inherit stale text.
  useEffect(() => {
    if (composerState !== COMPOSER_STATES.AWAITING_APPROVAL && modifyMode) {
      setModifyMode(false);
    }
  }, [composerState, modifyMode]);

  const editable = isPromptEditable({
    composerState,
    canExecute,
    modifyMode,
    iHoldClaim,
    busy: sending || approvalBusy,
    frozen,
  });
  // Attachments in Modify-mode require `pool_approval_handler` to forward
  // `attachmentStagingId` / `attachmentInput` into the resumed Step
  // Function — that backend extension lands separately. Until then the
  // composer keeps the Attach button visible but disabled during Modify
  // so the room still feels uniform without misleading the user about
  // what reaches the agent.
  const attachAllowed = composerState !== COMPOSER_STATES.AWAITING_APPROVAL || !modifyMode;
  const attachEnabled = editable && attachAllowed && !frozen;
  const sendReady = canSendNow({
    composerState,
    canExecute,
    modifyMode,
    iHoldClaim,
    promptText: prompt,
    hasUploading: attachments.hasUploading,
    busy: sending || approvalBusy,
    frozen,
    mcpGateReason,
  });
  const placeholder = placeholderFor({
    composerState,
    canExecute,
    modifyMode,
    iHoldClaim,
    peerLabel,
    frozen,
    frozenReason,
    mcpGateReason,
  });

  const fireSend = async () => {
    if (!sendReady) return;
    const text = prompt.trim();
    if (composerState === COMPOSER_STATES.IDLE) {
      await onSendPrompt(text, {
        stagingId: attachments.stagingId,
        attachments: attachments.descriptors,
        correlationId: attachments.stagingId, // reuse staging id as correlation key
      });
    } else if (composerState === COMPOSER_STATES.AWAITING_APPROVAL && modifyMode && iHoldClaim) {
      await onSendModify(text, {
        stagingId: attachments.stagingId,
        attachments: attachments.descriptors,
      });
      setModifyMode(false);
    }
    setPrompt('');
    attachments.reset();
  };

  const handleAttachClick = () => {
    if (!attachEnabled) return;
    fileInputRef.current?.click();
  };

  const handleFiles = (e) => {
    const files = e.target.files;
    if (files && files.length) attachments.addFiles(files);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const dismissComposerPromptTip = () => {
    try {
      sessionStorage.setItem(COMPOSER_PROMPT_TIP_KEY, '1');
    } catch {
      /* ignore private mode */
    }
    setComposerTipDismissed(true);
  };

  // Band priority while a run is active:
  //   1. builderCliWait → the interaction band (approval/question)
  //   2. activeBuilderRunId → "run in progress" band WITH the Stop action INSIDE
  //      the band (not a separate box) — always-present kill switch
  //   3. else → the dismissible heads-up tip
  const promptAlertBand = builderCliWait ? (
    <BuilderInteractionBand
      networkId={networkId}
      runId={builderCliWait.runId}
      poolId={poolId}
      waitEventId={builderCliWait.eventId}
      interaction={builderCliWait.interaction}
      promptSnippet={builderCliWait.prompt}
      onHandled={onBuilderCliHandled}
      readOnly={!canRespondApproval}
    />
  ) : (activeBuilderRunId && onStopRun) ? (
    <WorkbenchPromptAlertBand
      message={
        <span className="flex items-center gap-1.5">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
          </span>
          Builder run in progress…
        </span>
      }
      primaryLabel={stoppingRun ? 'Stopping…' : 'Stop'}
      onPrimary={stoppingRun ? undefined : onStopRun}
      primaryTone="danger"
    />
  ) : (composerTipDismissed || !roomPremise || roomIsRunning) ? null : (
    <WorkbenchPromptAlertBand
      message={
        <>
          <span className="font-medium text-gray-800">Working premise:</span>{' '}
          {roomPremise}
        </>
      }
      progressPct={
        typeof roomGuidance?.completion?.pct === 'number'
          ? roomGuidance.completion.pct
          : null
      }
      primaryLabel="Got it"
      onPrimary={dismissComposerPromptTip}
      onDismiss={dismissComposerPromptTip}
    />
  );

  return (
    <div className="shrink-0 px-6 pb-6">
      <div className={align === 'center' ? 'mx-auto max-w-3xl' : 'w-full'}>
        {composerState === COMPOSER_STATES.AWAITING_APPROVAL && pendingApproval ? (
          <ApprovalDeck
            pendingApproval={pendingApproval}
            iHoldClaim={iHoldClaim}
            peerHoldsClaim={peerHoldsClaim}
            peerLabel={peerLabel}
            modifyMode={modifyMode}
            approvalBusy={approvalBusy}
            frozen={frozen}
            frozenReason={frozenReason}
            onClaim={onClaim}
            onApprove={onApprove}
            onDeny={onDeny}
            onStartModify={() => setModifyMode(true)}
            onCancelModify={() => {
              setModifyMode(false);
              setPrompt('');
              attachments.reset();
            }}
            onDelegate={onDelegate}
            onEscalate={onEscalate}
          />
        ) : null}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            void fireSend();
          }}
          className="relative z-20"
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={handleFiles}
          />
          <WorkbenchPromptCard
            singleRow={!composerMultiline}
            alertBand={promptAlertBand}
            top={(
              <div className="flex flex-col gap-2">
                {attachments.items.length > 0 ? (
                  <AttachmentChips
                    items={attachments.items}
                    onRemove={attachments.removeItem}
                    disabled={!editable}
                  />
                ) : null}
                <textarea
                  ref={promptFieldRef}
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      void fireSend();
                    }
                  }}
                  disabled={!editable}
                  rows={1}
                  placeholder={placeholder}
                  aria-multiline="true"
                  className={getWorkbenchPromptInputClassName({ disabled: !editable })}
                  style={WORKBENCH_PROMPT_FIELD_STYLE}
                />
              </div>
            )}
            bottomLeft={(
              <div className="flex items-center gap-2">
                <ComposerPlusMenu
                  testId="room-composer-plus"
                  options={[
                    {
                      id: 'attachment',
                      label: 'Attachment',
                      icon: Paperclip,
                      type: 'action',
                      disabled: !attachEnabled,
                      onClick: handleAttachClick,
                    },
                  ]}
                />
                {composerState === COMPOSER_STATES.AWAITING_APPROVAL && modifyMode && iHoldClaim ? (
                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-900">
                    Modify mode — Send replies to the agent
                  </span>
                ) : null}
              </div>
            )}
            bottomRight={(
              <SendButton sendReady={sendReady} sending={sending} />
            )}
          />
          {/* Under-bar line: by default just the Room-guidance (✦) popover trigger —
              its popover already carries the completion basis, the working premise
              AND the suggested-next prompts, so surfacing them inline too is
              redundant chrome. Under VERBOSE, the completion-basis line + inline
              "Next:" quick-prompt chips are also shown for a one-glance path.
              Whole row hidden while the room is RUNNING — guidance is stale mid-run;
              it reappears when the room goes IDLE. */}
          {roomIsRunning ? null : (
          <div className="mt-1.5 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 px-1 text-[11px] leading-snug text-gray-400">
            {verbose && roomGuidance?.completion?.basis ? (
              <span>{roomGuidance.completion.basis}</span>
            ) : null}
            {verbose && editable && roomGuidance?.suggestedPrompts?.length ? (
              <span className="flex flex-wrap items-center gap-1.5">
                <span className="font-medium text-gray-500">Next:</span>
                {roomGuidance.suggestedPrompts.slice(0, 3).map((p, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => {
                      setPrompt(p);
                      setTimeout(() => promptFieldRef.current?.focus(), 0);
                    }}
                    className="max-w-[16rem] truncate rounded-full border border-gray-200 px-2 py-0.5 text-[11px] text-gray-600 transition-colors hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700"
                    title={p}
                  >
                    {p}
                  </button>
                ))}
              </span>
            ) : null}
            <ComposerHint
              inline
              bullets={COMPOSER_TIP_BULLETS}
              guidance={roomGuidance}
              guidanceAsOf={roomGuidanceAt}
              onUsePrompt={(text) => {
                // Advisory: drop the suggestion into the box; the user edits/sends.
                // Only when the composer is editable (IDLE or modify w/ claim).
                if (!editable) return;
                setPrompt(text);
                setTimeout(() => promptFieldRef.current?.focus(), 0);
              }}
            />
          </div>
          )}
        </form>
      </div>
    </div>
  );
}

function SendButton({ sendReady, sending }) {
  return (
    <button
      type="submit"
      disabled={!sendReady}
      className={`
        inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full font-medium transition-all
        ${
          !sendReady
            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
            : 'bg-gradient-to-r from-blue-500 to-purple-500 text-white hover:shadow-md hover:scale-105 active:scale-95'
        }
      `}
      aria-label="Send"
    >
      {sending ? (
        <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      ) : (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 10V3L4 14h7v7l9-11h-7z"
          />
        </svg>
      )}
    </button>
  );
}

function ApprovalDeck({
  pendingApproval,
  iHoldClaim,
  peerHoldsClaim,
  peerLabel,
  modifyMode,
  approvalBusy,
  frozen = false,
  frozenReason = '',
  onClaim,
  onApprove,
  onDeny,
  onStartModify,
  onCancelModify,
  onDelegate,
  onEscalate,
}) {
  const agentName = pendingApproval?.agentName || 'an agent';
  // When the room is frozen (PAUSED / RETIRED / ARCHIVED) every approval
  // action is disabled — backend rejects the wire calls anyway, but we
  // grey them out up front so the UI tells the truth.
  const lockTitle = frozen
    ? frozenReason || 'Frozen by lifecycle state — approvals are disabled.'
    : '';
  return (
    <div className="mb-2 rounded-2xl border border-amber-200 bg-amber-50/80 px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-amber-900">
          <span className="font-semibold">Approval required</span>
          {' · '}
          <span className="text-amber-800">{agentName}</span>
        </p>
        <div className="flex items-center gap-2 text-[11px] text-amber-900/80">
          {frozen ? (
            <span className="rounded-full bg-gray-200 px-2 py-0.5 font-medium text-gray-700" title={lockTitle}>
              Frozen
            </span>
          ) : iHoldClaim ? (
            <span className="rounded-full bg-emerald-100 px-2 py-0.5 font-medium text-emerald-900">
              You&apos;re reviewing
            </span>
          ) : peerHoldsClaim ? (
            <span className="rounded-full bg-amber-200/80 px-2 py-0.5 font-medium text-amber-900">
              {peerLabel || 'Someone'} is reviewing
            </span>
          ) : null}
        </div>
      </div>

      <div className="mt-2 flex flex-wrap gap-2">
        {!iHoldClaim ? (
          <button
            type="button"
            onClick={onClaim}
            disabled={frozen || approvalBusy || peerHoldsClaim}
            className={WORKBENCH_OUTLINE_BTN_TOOLBAR}
            title={
              frozen
                ? lockTitle
                : peerHoldsClaim
                  ? 'Another supervisor is reviewing'
                  : 'Take ownership of this approval'
            }
          >
            {peerHoldsClaim ? 'Claimed by peer' : 'Claim to review'}
          </button>
        ) : null}

        {!modifyMode ? (
          <>
            <button
              type="button"
              onClick={onApprove}
              disabled={frozen || approvalBusy || !iHoldClaim}
              className={WORKBENCH_OUTLINE_BTN_TOOLBAR}
              title={frozen ? lockTitle : undefined}
            >
              <Check className="h-3.5 w-3.5 opacity-70" aria-hidden />
              Approve
            </button>
            <button
              type="button"
              onClick={onDeny}
              disabled={frozen || approvalBusy || !iHoldClaim}
              className={WORKBENCH_OUTLINE_BTN_TOOLBAR}
              title={frozen ? lockTitle : undefined}
            >
              <XIcon className="h-3.5 w-3.5 opacity-70" aria-hidden />
              Deny
            </button>
            <button
              type="button"
              onClick={onStartModify}
              disabled={frozen || approvalBusy || !iHoldClaim}
              className={WORKBENCH_OUTLINE_BTN_TOOLBAR}
              title={frozen ? lockTitle : 'Edit / extend the agent draft and resume the run with your changes'}
            >
              <Pencil className="h-3.5 w-3.5 opacity-70" aria-hidden />
              Modify
            </button>
            {iHoldClaim ? (
              <button
                type="button"
                onClick={onDelegate}
                disabled={frozen || approvalBusy}
                className={WORKBENCH_OUTLINE_BTN_TOOLBAR}
                title={frozen ? lockTitle : undefined}
              >
                Delegate
              </button>
            ) : null}
            <button
              type="button"
              onClick={onEscalate}
              disabled={frozen || approvalBusy}
              className={WORKBENCH_OUTLINE_BTN_TOOLBAR}
              title={frozen ? lockTitle : 'Escalate to workspace admins'}
            >
              Escalate
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={onCancelModify}
            disabled={approvalBusy}
            className={WORKBENCH_OUTLINE_BTN_TOOLBAR}
          >
            Cancel modification
          </button>
        )}
      </div>
    </div>
  );
}
